<template>
  <div class="success-page">

    <!-- Animated background dots -->
    <div class="bg-dots" aria-hidden="true">
      <span v-for="i in 6" :key="i" class="dot" :class="'dot--' + i"></span>
    </div>

    <div class="success-card">

      <!-- Icon -->
      <div class="success-icon-wrap">
        <div class="success-icon-ring"></div>
        <font-awesome-icon icon="check" class="success-icon" />
      </div>

      <h1 class="success-title">Payment Successful</h1>
      <p class="success-sub">Your subscription has been activated. You're all set!</p>

      <!-- Detail rows -->
      <div class="detail-card">
        <div class="detail-row">
          <span class="detail-label">
            <font-awesome-icon icon="hashtag" class="detail-icon" /> Reference
          </span>
          <span class="detail-value detail-value--mono">{{ reference ?? '—' }}</span>
        </div>
        <div class="detail-divider"></div>

        <div class="detail-row">
          <span class="detail-label">
            <font-awesome-icon icon="credit-card" class="detail-icon" /> Amount Paid
          </span>
          <span class="detail-value detail-value--amount">{{ currency }}{{ formatPrice(amount) }}</span>
        </div>
        <div class="detail-divider"></div>

        <div class="detail-row">
          <span class="detail-label">
            <font-awesome-icon icon="box" class="detail-icon" /> Plan
          </span>
          <span class="detail-value">{{ plan ?? 'Subscription Plan' }}</span>
        </div>
        <div class="detail-divider"></div>

        <div class="detail-row">
          <span class="detail-label">
            <font-awesome-icon icon="calendar-alt" class="detail-icon" /> Next Billing Date
          </span>
          <span class="detail-value">{{ expiry_date ?? '—' }}</span>
        </div>
      </div>

      <!-- Verifying stripe badge -->
      <div v-if="verifying" class="verifying-badge">
        <font-awesome-icon icon="spinner" spin class="me-1" />
        Verifying payment...
      </div>

      <!-- Actions -->
      <div class="btn-group">
        <button class="btn-primary-action" @click="goDashboard">
          <font-awesome-icon icon="home" class="me-1" />
          Go to Dashboard
        </button>
        <button class="btn-secondary-action" @click="downloadInvoice">
          <font-awesome-icon icon="download" class="me-1" />
          Download Invoice
        </button>
      </div>

      <!-- Help -->
      <p class="help-text">
        Need help? <a href="mailto:support@mapiibs.co.uk" class="help-link">Contact support</a>
      </p>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import hostName from '../main';

export default {
  name: 'PaymentSuccess',

  data() {
    const countryCode = localStorage.getItem('country_code');
    return {
      reference:   null,
      amount:      0,
      currency:    countryCode === 'NG' ? '₦' : '£',
      plan:        'Subscription Plan',
      expiry_date: null,
      verifying:   false,
      backend_url: hostName,
    };
  },

  mounted() {
    const q = this.$route.query;
    this.reference   = q.ref    ?? null;
    this.amount      = q.amount ?? 0;
    this.plan        = q.plan   ?? 'Subscription Plan';
    this.expiry_date = q.expiry ?? null;

    // Stripe verification
    const sessionId = q.session_id;
    if (sessionId) {
      this.verifying = true;
      axios.post('/payment/verify-stripe-payment', { session_id: sessionId })
        .then(res => {
          if (res.data.status) {
            this.reference = res.data.data?.reference ?? this.reference;
          }
        })
        .catch(() => {})
        .finally(() => { this.verifying = false; });
    }
  },

  methods: {
    formatPrice(value) {
      return Number(value || 0).toLocaleString();
    },

    goDashboard() {
      this.$router.push('/');
    },

    downloadInvoice() {
      if (!this.reference) return;
      window.open(`${this.backend_url.hostName}api/subscription-invoice/${this.reference}`);
    },
  },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────────────────── */
.success-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f6ff 0%, #eef2ff 60%, #f4f9f4 100%);
  padding: 32px 16px;
  position: relative;
  overflow: hidden;
}

/* ── Background decorative dots ──────────────────────────────────────── */
.bg-dots { position:fixed; inset:0; pointer-events:none; z-index:0; }
.dot { position:absolute; border-radius:50%; opacity:.18; animation:float 8s ease-in-out infinite; }
.dot--1 { width:300px; height:300px; background:#004AAD; top:-80px;  left:-80px;  animation-delay:0s; }
.dot--2 { width:200px; height:200px; background:#16a34a; top:60%;    right:-60px; animation-delay:1.5s; }
.dot--3 { width:150px; height:150px; background:#004AAD; bottom:-40px; left:30%;  animation-delay:3s; }
.dot--4 { width:100px; height:100px; background:#16a34a; top:20%;    left:10%;   animation-delay:2s; }
.dot--5 { width:80px;  height:80px;  background:#004AAD; bottom:15%; right:20%;  animation-delay:0.5s; }
.dot--6 { width:60px;  height:60px;  background:#16a34a; top:45%;    left:45%;   animation-delay:4s; }
@keyframes float {
  0%, 100% { transform: translateY(0) scale(1); }
  50%       { transform: translateY(-20px) scale(1.04); }
}

/* ── Card ────────────────────────────────────────────────────────────── */
.success-card {
  position: relative;
  z-index: 1;
  background: #fff;
  border-radius: 20px;
  padding: 48px 40px 36px;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 24px 64px rgba(0,0,0,.11);
  text-align: center;
  animation: slideUp .4s ease;
}

@keyframes slideUp {
  from { opacity:0; transform:translateY(24px); }
  to   { opacity:1; transform:translateY(0); }
}

/* ── Icon ────────────────────────────────────────────────────────────── */
.success-icon-wrap {
  position: relative;
  width: 80px; height: 80px;
  margin: 0 auto 24px;
  display: flex; align-items: center; justify-content: center;
}
.success-icon-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(22,163,74,.12);
  animation: ping 2s cubic-bezier(0,0,.2,1) infinite;
}
.success-icon {
  position: relative;
  z-index: 1;
  width: 40px; height: 40px;
  background: #16a34a;
  color: #fff;
  border-radius: 50%;
  padding: 10px;
  font-size: 20px;
}
@keyframes ping {
  75%, 100% { transform: scale(1.5); opacity: 0; }
}

/* ── Title ───────────────────────────────────────────────────────────── */
.success-title { font-size:24px; font-weight:800; color:#1a2b45; margin:0 0 8px; }
.success-sub   { font-size:14px; color:#7a90b0; margin:0 0 28px; line-height:1.5; }

/* ── Detail card ─────────────────────────────────────────────────────── */
.detail-card    { background:#f8fafc; border:1px solid #e4eaf2; border-radius:12px; padding:4px 0; margin-bottom:28px; text-align:left; }
.detail-row     { display:flex; align-items:center; justify-content:space-between; padding:13px 18px; gap:12px; }
.detail-divider { height:1px; background:#f0f4f8; margin:0 18px; }
.detail-label   { display:flex; align-items:center; gap:8px; font-size:13px; color:#7a90b0; font-weight:500; white-space:nowrap; }
.detail-icon    { font-size:12px; width:14px; color:#b0bdd0; }
.detail-value   { font-size:13px; font-weight:700; color:#1a2b45; text-align:right; word-break:break-word; max-width:220px; }
.detail-value--mono   { font-family:monospace; font-size:12px; background:#f0f4f8; border-radius:5px; padding:2px 8px; }
.detail-value--amount { font-size:15px; color:#16a34a; }

/* ── Verifying badge ─────────────────────────────────────────────────── */
.verifying-badge { display:inline-flex; align-items:center; gap:6px; background:#fff7ed; border:1px solid #fed7aa; border-radius:20px; padding:5px 14px; font-size:12.5px; color:#e08c00; font-weight:600; margin-bottom:18px; }

/* ── Buttons ─────────────────────────────────────────────────────────── */
.btn-group { display:flex; gap:10px; margin-bottom:18px; }
.btn-primary-action {
  flex: 1;
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  background: #004AAD; color: #fff; border: none;
  padding: 12px 16px; border-radius: 9px;
  font-size: 13.5px; font-weight: 700; cursor: pointer;
  transition: background .15s, transform .1s;
}
.btn-primary-action:hover { background: #003b8e; }
.btn-primary-action:active { transform: scale(.97); }
.btn-secondary-action {
  flex: 1;
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  background: #fff; color: #1a2b45;
  border: 1px solid #dde5f0;
  padding: 12px 16px; border-radius: 9px;
  font-size: 13.5px; font-weight: 700; cursor: pointer;
  transition: background .15s, border-color .15s;
}
.btn-secondary-action:hover { background: #f4f7fb; border-color:#c5d6f5; color:#004AAD; }

.me-1 { margin-right:5px; }

/* ── Help text ───────────────────────────────────────────────────────── */
.help-text { font-size:12.5px; color:#b0bdd0; margin:0; }
.help-link  { color:#004AAD; font-weight:600; text-decoration:none; }
.help-link:hover { text-decoration:underline; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .success-card { padding:36px 20px 28px; }
  .btn-group    { flex-direction:column; }
}
</style>